import { useEffect, useState, useCallback } from 'react';
import { useChatStore } from '../stores/chatStore';
import { clearStorageIfNeeded } from '../utils/storageUtils';

/**
 * Hook to handle the initial setup of the chat application.
 * Fetches config, parses URL params, sets up global listeners, and starts the session.
 * @returns An object containing initialization state, e.g., { showDebug, setShowDebug }
 */
export function useChatInitialization() {
  const { fetchConfig, startSession } = useChatStore();
  const [showDebug, setShowDebug] = useState(false);
  const [debugSessionId, setDebugSessionId] = useState<string | null>(null); // Keep this local if only for debug panel

  // Define handleKeyDown within the hook or pass it if needed globally
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Enable debug mode with Ctrl+Shift+D
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
      setShowDebug(current => !current);
    }
  }, []);

  useEffect(() => {
    async function initialize() {
      // Load configuration
      await fetchConfig();
      
      // Check URL params for debug mode, session ID, fonts
      const urlParams = new URLSearchParams(window.location.search);
      const debugParam = urlParams.get('debug');
      if (debugParam === 'true') {
        setShowDebug(true);
      }
      
      const sessionIdParam = urlParams.get('sessionId');
      if (sessionIdParam) {
        setDebugSessionId(sessionIdParam); // Store the debug session ID if found
      }

      const fontParam = urlParams.get('font');
      if (fontParam) {
        document.documentElement.style.setProperty('--font-primary', fontParam);
        document.documentElement.style.setProperty('--font-secondary', fontParam);
      }
      
      // Set up keyboard handlers
      document.addEventListener('keydown', handleKeyDown);
      
      // Check if this is a refresh or new page load and clear storage if hostname changed
      clearStorageIfNeeded();
      
      // Start session
      startSession();
      
      // Cleanup listener on unmount
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    
    initialize();
    // Dependencies: fetchConfig, startSession, handleKeyDown. handleKeyDown is stable due to useCallback.
  }, [fetchConfig, startSession, handleKeyDown]); 

  return { showDebug, setShowDebug, debugSessionId };
} 